import React, { useState, useMemo, useEffect } from "react";
import { API_BASE } from "../api/config";
import { useTable, useSortBy } from "react-table";
import { useParams } from "react-router-dom";

function KotsDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { branch } = useParams();

  const statusMap = {
    1: "Placed",
    2: "Accepted",
    3: "Ready",
    4: "Served",
    5: "Cancel",
  };

  const statusColors = {
    Placed: "bg-yellow-500",
    Accepted: "bg-green-500",
    Ready: "bg-blue-500",
    Served: "bg-green-500",
    Cancel: "bg-red-500",
  };

  const calcProcessingTime = (placed, served) => {
    if (!placed || !served) return "-";
    const start = new Date(`1970-01-01T${placed}`);
    const end = new Date(`1970-01-01T${served}`);
    const diffMs = end - start;
    if (isNaN(diffMs) || diffMs < 0) return "-";
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const fetchKots = async () => {
    try {
      const res = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=get_today_kots&condition=branch&branch=${encodeURIComponent(
          branch
        )}`
      );
      const result = await res.json();

      // ✅ make sure we have an array
      const rows = Array.isArray(result) ? result : result?.data || [];
      const formatted = rows.map((item, index) => ({
        sno: index + 1,
        table_name: item.table_name,
        item_name: item.item_name,
        item_qty: item.item_qty,
        kot_running_status: statusMap[item.kot_running_status] || "Unknown",
        raw_status: parseInt(item.kot_running_status, 10),
        initiated: item.kot_time,
        received: item.recieved_time,
        ready: item.ready_time,
        delivered: item.delivered_time,
        cancel: item.cancel_time,
        raw_id: item.id,
        processing_time: calcProcessingTime(item.kot_time, item.delivered_time),
      }));

      setData(formatted);              // ✅ overwrite with empty array if no rows
      setLastUpdated(new Date().toLocaleTimeString());
      setLoading(false);
    } catch (err) {
      console.error("Error fetching KOTs:", err);
      setData([]);                     // ✅ clear data on error
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKots();
    const interval = setInterval(fetchKots, 5000);
    return () => clearInterval(interval);
  }, [branch]);                        // ✅ refetch if branch param changes

  const columns = useMemo(
    () => [
      { Header: "S.No", accessor: "sno" },
      { Header: "Table", accessor: "table_name" },
      { Header: "Item", accessor: "item_name" },
      { Header: "Qty", accessor: "item_qty" },
      {
        Header: "Status",
        accessor: "kot_running_status",
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 text-xs rounded text-white ${
              statusColors[value] || "bg-gray-500"
            }`}
          >
            Order {value}
          </span>
        ),
      },
      { Header: "Placed", accessor: "initiated" },
      { Header: "Accepted", accessor: "received" },
      { Header: "Ready", accessor: "ready" },
      { Header: "Served", accessor: "delivered" },
      {
        Header: "Processing Time",
        accessor: "processing_time",
        Cell: ({ value }) => (
          <span className="px-2 py-1 text-xs rounded text-black bg-pink-300 font-semibold">
            {value}
          </span>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable({ columns, data }, useSortBy);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white bg-gradient-to-b from-blue-900 to-black">
        <p>Loading KOT details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-black p-6 text-white text-sm">
      <h1 className="text-xl font-bold mb-1">
        KOT Details – Last updated at: {lastUpdated || "—"}
      </h1>

      <div className="overflow-x-auto bg-white bg-opacity-10 rounded mt-2">
        <table
          {...getTableProps()}
          className="min-w-full border border-gray-300 text-xs"
        >
          <thead className="bg-gray-200 bg-opacity-20 text-white">
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    className="px-2 py-1 border cursor-pointer"
                  >
                    {col.render("Header")}
                    <span>
                      {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-4 text-gray-300"
                >
                  No KOTs for this branch
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="hover:bg-gray-100 hover:bg-opacity-20"
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="px-2 py-1 border">
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default KotsDashboard;
